import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useInstallApp } from "hooks/useInstallApp";

const DISMISS_KEY = "healthdesk.installPrompt.dismissed";

const BoolChip = ({ value }) => (
  <Chip
    label={value ? "true" : "false"}
    size="small"
    color={value ? "success" : "default"}
    variant="outlined"
  />
);

const Row = ({ label, value }) => (
  <ListItem disableGutters>
    <ListItemText
      primary={label}
      secondary={value}
      secondaryTypographyProps={{ sx: { wordBreak: "break-all" } }}
    />
  </ListItem>
);

/**
 * Not linked from any nav — reachable at /dashboard/debug-install for anyone
 * chasing down why the install nudge or nav item isn't showing.
 */
const DebugInstallPage = () => {
  const installApp = useInstallApp();
  const [events, setEvents] = useState([]);
  const [manifestInfo, setManifestInfo] = useState(null);
  const [swState, setSwState] = useState("checking...");
  const [dismissal, setDismissal] = useState(null);

  useEffect(() => {
    const log = (label) =>
      setEvents((prev) => [...prev, `${new Date().toLocaleTimeString()} — ${label}`]);

    const onBeforeInstallPrompt = () => log("beforeinstallprompt fired");
    const onInstalled = () => log("appinstalled fired");
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);

    log("debug page mounted");

    try {
      const raw = window.localStorage.getItem(DISMISS_KEY);
      setDismissal(raw ? JSON.parse(raw) : null);
    } catch (error) {
      setDismissal(`localStorage read failed: ${error.message}`);
    }

    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (!manifestLink) {
      setManifestInfo("no <link rel=\"manifest\"> found in document head");
    } else {
      fetch(manifestLink.href)
        .then((res) => res.json())
        .then((json) =>
          setManifestInfo({
            href: manifestLink.href,
            name: json.name,
            short_name: json.short_name,
            display: json.display,
            start_url: json.start_url,
            icons: (json.icons || []).length,
          }),
        )
        .catch((error) =>
          setManifestInfo(`fetch/parse failed: ${error.message}`),
        );
    }

    if (navigator.serviceWorker) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) =>
          setSwState(
            regs.length
              ? regs
                  .map((r) => `${r.active ? "active" : "inactive"} @ ${r.scope}`)
                  .join(", ")
              : "no service worker registered",
          ),
        )
        .catch((error) => setSwState(`error: ${error.message}`));
    } else {
      setSwState("navigator.serviceWorker unsupported");
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  return (
    <Box sx={{ pb: 4 }}>
      <DashboardPageHeader
        title="Debug: Install App"
        subtitle="Not linked anywhere — bookmark it if you need it again."
        sx={{ mb: 2 }}
      />

      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          useInstallApp()
        </Typography>
        <List dense>
          <ListItem disableGutters>
            <ListItemText primary="method" secondary={installApp.method} />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText
              primary="installed"
              secondary={<BoolChip value={installApp.installed} />}
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText
              primary="supported"
              secondary={<BoolChip value={installApp.supported} />}
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText
              primary="canPromptDirectly"
              secondary={<BoolChip value={installApp.canPromptDirectly} />}
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText
              primary="nudgeAllowed"
              secondary={<BoolChip value={installApp.nudgeAllowed} />}
            />
          </ListItem>
        </List>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The nav item ("Add to home screen") only hides on{" "}
          <code>!supported || installed</code>. If <code>supported</code> is
          false and <code>method</code> is "unsupported", Chrome never fired{" "}
          <code>beforeinstallprompt</code> — check the event log and PWA
          installability checklist below.
        </Typography>
      </Card>

      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Raw browser signals
        </Typography>
        <List dense>
          <Row label="userAgent" value={navigator.userAgent} />
          <Row label="platform" value={navigator.platform} />
          <Row label="maxTouchPoints" value={String(navigator.maxTouchPoints)} />
          <Row
            label="matchMedia(display-mode: standalone)"
            value={String(
              window.matchMedia?.("(display-mode: standalone)").matches,
            )}
          />
          <Row
            label="navigator.standalone"
            value={String(navigator.standalone)}
          />
          <Row
            label="matchMedia(pointer: coarse)"
            value={String(window.matchMedia?.("(pointer: coarse)").matches)}
          />
          <Row label="isSecureContext" value={String(window.isSecureContext)} />
        </List>
      </Card>

      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Manifest &amp; service worker
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {typeof manifestInfo === "string"
            ? manifestInfo
            : manifestInfo
              ? JSON.stringify(manifestInfo, null, 2)
              : "loading..."}
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="body2">{swState}</Typography>
      </Card>

      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Dismissal state (localStorage)
        </Typography>
        <Typography variant="body2">
          {dismissal ? JSON.stringify(dismissal) : "no dismissal recorded"}
        </Typography>
      </Card>

      <Card sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Event log
        </Typography>
        <List dense>
          {events.map((event, index) => (
            <ListItem disableGutters key={index}>
              <ListItemText primary={event} />
            </ListItem>
          ))}
        </List>
        <Typography variant="body2" color="text.secondary">
          If "beforeinstallprompt fired" never appears after a few seconds on
          this page, Chrome has already decided the app isn't installable (or
          decided so before this listener attached — try a hard reload).
        </Typography>
      </Card>
    </Box>
  );
};

export default DebugInstallPage;
